import React, { useState, useEffect } from 'react'
import axios from 'axios'
import {
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer
} from 'recharts'
import { convertTimestampToDate, convertSecondsToDuration } from './../utils'
import Loading from './Loading'

function CountChart({data}) {
  const color = "#a2cffe"
  return (
    <ResponsiveContainer width="100%" height={200}>
      <BarChart
        width={730}
        height={250}
        data={data}
      >
        <CartesianGrid stroke="#f5f5f5" />
        <XAxis
          tick={{ fontSize: 10, fill: "#6699cc" }}
          dataKey="time" scale="time" type="number"
          domain={['dataMin - 2000000', 'dataMax + 2000000']}
          tickFormatter={tick => convertTimestampToDate(tick, true, false)}
        />
        <YAxis
          tick={{ fontSize: 10, fill: "#6699cc" }}
        />
        <Tooltip
         labelStyle={{ fontSize: 10 }}
         itemStyle={{ fontSize: 10 }}
         labelFormatter={tick => convertTimestampToDate(tick, true, false)}
        />
        <Bar dataKey="count" stroke={color} strokeWidth={2} fillOpacity={0.5} fill={color}/>
      </BarChart>
    </ResponsiveContainer>
  )
}

function RateChart({data}) {
  const postfixY = "°C/hour"
  const color = "#fdaa48"
  return (
    <ResponsiveContainer width="100%" height={200}>
      <AreaChart data={data}>
        <defs>
          <linearGradient id="ratecolor" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={color} stopOpacity={0.8}/>
            <stop offset="95%" stopColor={color} stopOpacity={0}/>
          </linearGradient>
        </defs>
        <CartesianGrid stroke="#f5f5f5" />
        <XAxis
          tick={{ fontSize: 10, fill: "#6699cc" }}
          dataKey="time" scale="time" type="number"
          domain={['auto', 'auto']}
          tickFormatter={tick => convertTimestampToDate(tick, true, false)}
        />
        <YAxis
          tick={{ fontSize: 10, fill: "#6699cc" }}
          tickFormatter={tick => tick + postfixY}
          domain={[0, "dataMax + 10"]}
        />
        <Tooltip
          labelStyle={{ fontSize: 10 }}
          itemStyle={{ fontSize: 10 }}
          labelFormatter={tick => convertTimestampToDate(tick, true, false)}
        />
        <Area connectNulls dataKey="rate" stroke={color} fillOpacity={1} fill="url(#ratecolor)"/>
      </AreaChart>
    </ResponsiveContainer>
  )
}

function DurationChart({data}) {
  const color = "#ff474c"
  return (
    <ResponsiveContainer width="100%" height={200}>
      <AreaChart data={data}>
        <defs>
          <linearGradient id="durationcolor" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={color} stopOpacity={0.8}/>
            <stop offset="95%" stopColor={color} stopOpacity={0}/>
          </linearGradient>
        </defs>
        <CartesianGrid stroke="#f5f5f5" />
        <XAxis
          tick={{ fontSize: 10, fill: "#6699cc" }}
          dataKey="time" scale="time" type="number"
          domain={['auto', 'auto']}
          tickFormatter={tick => convertTimestampToDate(tick, true, false)}
        />
        <YAxis
          tick={{ fontSize: 10, fill: "#6699cc" }}
          tickFormatter={tick => convertSecondsToDuration(tick * 3600, false)}
          domain={['auto', 'auto']}
        />
        <Tooltip
          labelStyle={{ fontSize: 10 }}
          itemStyle={{ fontSize: 10 }}
          labelFormatter={tick => convertTimestampToDate(tick, true, false)}
          formatter={tick => convertSecondsToDuration(tick * 3600, false)}
        />
        <Area connectNulls dataKey="duration" stroke={color} fillOpacity={1} fill="url(#durationcolor)"/>
      </AreaChart>
    </ResponsiveContainer>
  )
}

function StartValueChart({data}) {
  const postfixY = "°C"
  const color = "#c9ff27"
  return (
    <ResponsiveContainer width="100%" height={200}>
      <AreaChart data={data}>
        <defs>
          <linearGradient id="startcolor" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={color} stopOpacity={0.8}/>
            <stop offset="95%" stopColor={color} stopOpacity={0}/>
          </linearGradient>
        </defs>
        <CartesianGrid stroke="#f5f5f5" />
        <XAxis
          tick={{ fontSize: 10, fill: "#6699cc" }}
          dataKey="time" scale="time" type="number"
          domain={['auto', 'auto']}
          tickFormatter={tick => convertTimestampToDate(tick, true, false)}
        />
        <YAxis
          tick={{ fontSize: 10, fill: "#6699cc" }}
          tickFormatter={tick => tick + postfixY}
          domain={['auto', 'auto']}
        />
        <Tooltip
          labelStyle={{ fontSize: 10 }}
          itemStyle={{ fontSize: 10 }}
          labelFormatter={tick => convertTimestampToDate(tick, true, false)}
        />
        <Area connectNulls dataKey="start" stroke={color} fillOpacity={1} fill="url(#startcolor)"/>
      </AreaChart>
    </ResponsiveContainer>
  )
}

function StatisticsCharts({facility, records}) {
  const data = records.time.map(t => {
    return {
      'time': parseFloat(t),
      'count': records.count[t],
      'rate': records.rate[t] ? records.rate[t].toFixed(1) : null,
      'start': records.start[t] ? records.start[t].toFixed(1) : null,
      'duration': records.duration[t] ? records.duration[t] : null
  }})
  return (
    <>
    <div className="row mb-4">
      <div className="col">
        <div className="row">
          <h4 className="mx-auto text-muted">Treatments</h4>
        </div>
        <div className="row">
          <CountChart data={data} />
        </div>
      </div>
      <div className="col">
        <div className="row">
          <h4 className="mx-auto text-muted">Heating rate</h4>
        </div>
        <div className="row">
          <RateChart data={data} />
        </div>
      </div>
    </div>
    <div className="row mb-4">
      <div className="col">
        <div className="row">
          <h4 className="mx-auto text-muted">Leadtime</h4>
        </div>
        <div className="row">
          <DurationChart data={data} />
        </div>
      </div>
      <div className="col">
        <div className="row">
          <h4 className="mx-auto text-muted">Start temperature</h4>
        </div>
        <div className="row">
          <StartValueChart data={data} />
        </div>
      </div>
    </div>
    </>
  )
}

export default function Statistics({ facility }) {
  const [records, setRecords] = useState({})
  const [isLoaded, setIsLoaded] = useState(false)

  useEffect(() => {
    if (!facility) return
    const fetchStats = async () => {
      const rsp = await axios.get(`/api/v1/report/stats/${facility.name}`)
      setRecords(rsp.data?.result)
      setIsLoaded(Object.keys(rsp.data?.result).length !== 0)
    }
    fetchStats()
  }, [facility])

  return (
    <div className='container-fluid'>
      <div className="row">
        <div className="col card">
          <div className="card-header bg-transparent">
            <div className="row align-items-center">
              <div className="col">
                <h5 className="card-title text-uppercase text-muted mb-0">{facility.name}</h5>
                <span className="h2 font-weight-bold mb-0">Statistics</span>
              </div>
            </div>
          </div>
          <div className="card-body pt-0 pb-1">
            {isLoaded ? <StatisticsCharts facility={facility} records={records}/> : <Loading/>}
          </div>
        </div>
      </div>
    </div>
  )
}